import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  CircularProgress,
  ClickAwayListener,
  InputBase,
  Paper,
  Popper,
  Typography,
  styled,
} from "@mui/material";
import {
  Search as SearchIcon,
  Person as PersonIcon,
  FitnessCenter as TemplatesIcon,
  Restaurant as FoodIcon,
  EmojiEvents as ExercisesIcon,
  Description as FormsIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  GroupsOutlined as TrainersIcon,
  SpaOutlined as SupplementsIcon,
} from "@mui/icons-material";

import { useAuth } from "../../contexts/AuthContext";
import { useCompany } from "../../contexts/CompanyContext";
import { canRouteTo, viewerFromAuth } from "../../enums/Navigation";
import { getLiveClients } from "../../services/TraineeService";
import { getTemplates } from "../../services/TemplatesService";
import { getExercises } from "../../services/ExcersiceService";
import { searchFoods } from "../../services/FoodDictionaryService";
import { searchSupplements } from "../../services/SupplementService";
import { imageUrl } from "../../utils/imageUrl";

const SearchWrap = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  height: 34,
  padding: "0 12px",
  width: "100%",
  maxWidth: 420,
  borderRadius: 10,
  border: `1px solid ${theme.palette.neutral[100]}`,
  backgroundColor: theme.palette.neutral[50],
  transition: "border-color 0.14s ease, box-shadow 0.14s ease, background-color 0.14s ease",
  "&:hover": { borderColor: theme.palette.neutral[200], backgroundColor: theme.palette.background.paper },
  "&:focus-within": {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.background.paper,
    boxShadow: `0 0 0 3px rgba(1,187,167,0.08)`,
  },
}));

const ResultRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "8px 12px",
  borderRadius: 8,
  cursor: "pointer",
  transition: "background-color 0.12s ease",
  "&:hover, &[data-active='true']": {
    backgroundColor: theme.palette.primary.surface,
  },
}));

const TypeChip = styled(Box)(({ theme }) => ({
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: theme.palette.text.tertiary,
  border: `1px solid ${theme.palette.neutral[100]}`,
  borderRadius: 4,
  padding: "2px 6px",
  flexShrink: 0,
}));

const SectionLabel = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: theme.palette.neutral[500],
  padding: "10px 12px 6px",
}));

/**
 * The pages the palette can jump to. Which of them a given user is OFFERED is not decided here —
 * every entry is filtered through `canRouteTo` (see below), so this list only says "this page is
 * worth a shortcut" and carries the copy/icon the registry has no opinion about.
 *
 * <p>Kept as its own list rather than derived from NAVIGATION on purpose: it is a curated subset
 * (nobody searches for /app/build-console) and it needs a label, Hebrew/English aliases and an icon
 * per entry, none of which belong in a permissions registry.
 */
const PAGE_SHORTCUTS = [
  { path: "/app/dashboard", label: "דשבורד", aliases: ["dashboard"], icon: SettingsIcon },
  { path: "/app/clients", label: "לקוחות", aliases: ["clients", "מתאמן", "מתאמנים"], icon: PersonIcon },
  { path: "/app/trainers", label: "מאמנים", aliases: ["trainers"], icon: TrainersIcon },
  { path: "/app/templates", label: "תוכניות אימון", aliases: ["templates", "workouts", "programs"], icon: TemplatesIcon },
  { path: "/app/exercises", label: "תרגילים", aliases: ["exercises"], icon: ExercisesIcon },
  { path: "/app/food-dictionary", label: "מאגר מזון", aliases: ["food", "מזון"], icon: FoodIcon },
  { path: "/app/supplements", label: "מאגר תוספים", aliases: ["supplements"], icon: SupplementsIcon },
  { path: "/app/menu-templates", label: "תבניות תפריטים", aliases: ["menus", "templates"], icon: FoodIcon },
  { path: "/app/forms", label: "טפסים", aliases: ["forms"], icon: FormsIcon },
  { path: "/app/guides", label: "מדריכים", aliases: ["guides"], icon: FormsIcon },
  { path: "/app/notifications", label: "התראות", aliases: ["notifications"], icon: NotificationsIcon },
  { path: "/app/check-ins", label: "צ'ק-אין", aliases: ["check-ins", "checkins"], icon: FormsIcon },
  { path: "/app/setting", label: "הגדרות", aliases: ["settings"], icon: SettingsIcon },
];

const matchShortcut = (q, shortcut) => {
  const lq = q.toLowerCase();
  if (shortcut.label.toLowerCase().includes(lq)) return true;
  return shortcut.aliases.some((a) => a.toLowerCase().includes(lq));
};

const ROW_LIMIT = 4;

const TopBarSearch = ({ sx }) => {
  const navigate = useNavigate();
  const auth = useAuth();
  const { company } = useCompany();

  // The audience the page shortcuts are resolved against — the same registry the side nav and
  // PermissionGuard read, so the palette can't offer a screen the guard would bounce you off.
  const viewer = useMemo(() => viewerFromAuth(auth), [auth]);

  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [results, setResults] = useState({
    clients: [],
    templates: [],
    exercises: [],
    foods: [],
    supplements: [],
  });
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  // Debounce
  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(id);
  }, [query]);

  // Fetch results from all entity types in parallel
  useEffect(() => {
    if (debounced.length < 2) {
      setResults({ clients: [], templates: [], exercises: [], foods: [], supplements: [] });
      return;
    }
    let cancelled = false;
    setLoading(true);
    const companyId = company?.id != null ? company.id : null;

    const safe = (p) => p.catch(() => ({ data: null }));

    Promise.all([
      safe(getLiveClients(0, ROW_LIMIT, debounced, null, null, null, companyId, null, null)),
      safe(getTemplates(0, ROW_LIMIT, null, null, debounced, null, null, null, null, null, null, companyId)),
      safe(getExercises(0, ROW_LIMIT, debounced)),
      safe(searchFoods({ page: 0, size: ROW_LIMIT, keyword: debounced })),
      safe(searchSupplements({ page: 0, size: ROW_LIMIT, keyword: debounced })),
    ]).then(([clientsRes, templatesRes, exercisesRes, foodsRes, supplementsRes]) => {
      if (cancelled) return;
      const pickList = (res) => {
        const d = res?.data;
        if (!d) return [];
        return d.content || d.clients || d.templates || d.exercises || d.foods || d.supplements || d.items || (Array.isArray(d) ? d : []);
      };
      setResults({
        clients: pickList(clientsRes),
        templates: pickList(templatesRes),
        exercises: pickList(exercisesRes),
        foods: pickList(foodsRes),
        supplements: pickList(supplementsRes),
      });
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [debounced, company]);

  const shortcuts = useMemo(() => {
    if (!debounced || debounced.length < 1) return [];
    // `canRouteTo`, not `canAccessPath`: a palette answers "can I OPEN this?", which is the same
    // question PermissionGuard asks a moment later when the row is clicked. The two differ only for
    // self-scoped screens — `/app/setting` is deliberately kept out of the company-less personas'
    // sidebar, but every persona edits their own profile there and may open it, so a super-admin
    // typing "הגדרות" should still be offered it.
    //
    // THIS IS PRESENTATION, NOT ENFORCEMENT. The server is the boundary and re-decides every
    // request; filtering here only spares people shortcuts to screens that would refuse them (and
    // stops the palette advertising screens they have no business knowing about).
    return PAGE_SHORTCUTS.filter(
      (s) => canRouteTo(s.path, viewer) && matchShortcut(debounced, s)
    ).slice(0, 5);
  }, [debounced, viewer]);

  const showResults = open && query.trim().length > 0;

  const sections = useMemo(() => {
    const list = [];
    if (results.clients.length) {
      list.push({
        key: "clients",
        label: "לקוחות",
        typeLabel: "לקוח",
        rows: results.clients.map((c) => {
          const id = c.traineeId ?? c.id;
          return {
            id,
            label: c.name,
            sub: c.email,
            photoUrl: c.photoUrl,
            path: `/app/clients/${id}`,
            IconComp: PersonIcon,
          };
        }),
      });
    }
    if (results.templates.length) {
      list.push({
        key: "templates",
        label: "תוכניות אימון",
        typeLabel: "תוכנית",
        rows: results.templates.map((t) => ({
          id: t.id,
          label: t.nameHeb || t.hebName || t.name || t.title,
          sub: t.structure || t.description,
          path: `/app/templates/${t.id}`,
          IconComp: TemplatesIcon,
        })),
      });
    }
    if (results.exercises.length) {
      list.push({
        key: "exercises",
        label: "תרגילים",
        typeLabel: "תרגיל",
        rows: results.exercises.map((ex) => ({
          id: ex.id,
          label: ex.nameHeb || ex.hebName || ex.name,
          sub: ex.bodyPart || ex.primaryMuscle,
          path: `/app/exercises/${ex.id}`,
          IconComp: ExercisesIcon,
        })),
      });
    }
    if (results.foods.length) {
      list.push({
        key: "foods",
        label: "מאגר מזון",
        typeLabel: "מזון",
        rows: results.foods.map((f) => ({
          id: f.id,
          label: f.nameHeb || f.hebName || f.name,
          sub: f.brand || f.nutritionType,
          path: `/app/food-dictionary/${f.id}`,
          IconComp: FoodIcon,
        })),
      });
    }
    if (results.supplements.length) {
      list.push({
        key: "supplements",
        label: "תוספים",
        typeLabel: "תוסף",
        rows: results.supplements.map((s) => ({
          id: s.id,
          label: s.nameHeb || s.hebName || s.name,
          sub: s.brand,
          path: `/app/supplements/${s.id}`,
          IconComp: SupplementsIcon,
        })),
      });
    }
    if (shortcuts.length) {
      list.push({
        key: "pages",
        label: "עמודים",
        typeLabel: "עמוד",
        rows: shortcuts.map((s) => ({
          id: s.path,
          label: s.label,
          path: s.path,
          IconComp: s.icon,
        })),
      });
    }
    return list;
  }, [results, shortcuts]);

  const flatRows = useMemo(
    () => sections.flatMap((s) => s.rows.map((r) => ({ ...r, sectionKey: s.key }))),
    [sections]
  );

  useEffect(() => {
    if (activeIndex >= flatRows.length) setActiveIndex(0);
  }, [flatRows, activeIndex]);

  const goTo = (row) => {
    setOpen(false);
    setQuery("");
    setDebounced("");
    navigate(row.path);
  };

  const handleKeyDown = (e) => {
    if (!showResults) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, flatRows.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const row = flatRows[activeIndex];
      if (row) goTo(row);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box sx={{ position: "relative", width: "100%", maxWidth: 480, ...sx }} ref={wrapRef}>
        <SearchWrap onClick={() => inputRef.current?.focus()}>
          <SearchIcon sx={{ fontSize: 18, color: "text.tertiary" }} />
          <InputBase
            inputRef={inputRef}
            placeholder="חפש לקוח, תוכנית, תרגיל, מזון…"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
              setActiveIndex(0);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
            sx={{
              flex: 1,
              fontSize: 13,
              color: "text.primary",
              "& input::placeholder": { color: "text.tertiary", opacity: 1 },
            }}
          />
          {loading && <CircularProgress size={14} thickness={5} sx={{ color: "primary.main" }} />}
        </SearchWrap>

        <Popper
          open={showResults}
          anchorEl={wrapRef.current}
          placement="bottom-start"
          style={{ zIndex: 1300, width: wrapRef.current?.offsetWidth }}
          modifiers={[{ name: "offset", options: { offset: [0, 6] } }]}
        >
          <Paper
            elevation={0}
            sx={{
              border: (theme) => `1px solid ${theme.palette.neutral[100]}`,
              borderRadius: 2,
              boxShadow: 3,
              p: 0.5,
              maxHeight: 460,
              overflowY: "auto",
            }}
          >
            {!loading && flatRows.length === 0 && (
              <Box sx={{ p: 2, textAlign: "center", color: "text.tertiary", fontSize: 13 }}>
                לא נמצאו תוצאות
              </Box>
            )}

            {(() => {
              let rowIndex = 0;
              return sections.map((section) => (
                <Box key={section.key}>
                  <SectionLabel>{section.label}</SectionLabel>
                  {section.rows.map((row) => {
                    const i = rowIndex++;
                    const Icon = row.IconComp;
                    const showAvatar = section.key === "clients";
                    return (
                      <ResultRow
                        key={`${section.key}-${row.id}`}
                        data-active={activeIndex === i}
                        onMouseEnter={() => setActiveIndex(i)}
                        onClick={() => goTo(row)}
                      >
                        {showAvatar ? (
                          <Avatar
                            src={imageUrl(row.photoUrl, 28) || undefined}
                            sx={{
                              width: 28,
                              height: 28,
                              fontSize: 12,
                              bgcolor: "primary.surface",
                              color: "primary.dark",
                            }}
                          >
                            {row.label?.[0] || "?"}
                          </Avatar>
                        ) : (
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: 1.5,
                              bgcolor: "primary.surface",
                              color: "primary.main",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <Icon sx={{ fontSize: 16 }} />
                          </Box>
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 500,
                              color: "text.primary",
                              lineHeight: 1.3,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {row.label}
                          </Typography>
                          {row.sub && (
                            <Typography
                              sx={{
                                fontSize: 11,
                                color: "text.tertiary",
                                lineHeight: 1.3,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {row.sub}
                            </Typography>
                          )}
                        </Box>
                        <TypeChip>{section.typeLabel}</TypeChip>
                      </ResultRow>
                    );
                  })}
                </Box>
              ));
            })()}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default TopBarSearch;
